import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Intersection Observer hook for lazy animations / lazy loading
 * @param {number} threshold - visibility threshold (0 to 1)
 * @returns {[React.RefObject, boolean]}
 */
export function useInView(threshold = 0.12) {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isInView];
}

/**
 * Scroll position hook for navbar state
 * @param {number} offset - scroll threshold in pixels
 * @returns {boolean}
 */
export function useScrolled(offset = 50) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > offset);
    window.addEventListener('scroll', handler, { passive: true });
    handler();
    return () => window.removeEventListener('scroll', handler);
  }, [offset]);

  return scrolled;
}

/**
 * Media query hook for responsive logic
 * @param {string} query - CSS media query string
 * @returns {boolean}
 */
export function useMediaQuery(query) {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    setMatches(media.matches);
    const handler = (e) => setMatches(e.matches);
    media.addEventListener('change', handler);
    return () => media.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

/**
 * Smooth scroll to element by ID
 * @returns {Function}
 */
export function useScrollTo() {
  return useCallback((id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
}

/**
 * Animated counter hook
 * @param {number} end - target number
 * @param {boolean} shouldAnimate - trigger animation
 * @returns {number}
 */
export function useCounter(end, shouldAnimate) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!shouldAnimate) return;
    let value = 0;
    const step = Math.max(1, Math.ceil(end / 60));
    const timer = setInterval(() => {
      value += step;
      if (value >= end) {
        setCount(end);
        clearInterval(timer);
      } else {
        setCount(value);
      }
    }, 25);
    return () => clearInterval(timer);
  }, [shouldAnimate, end]);

  return count;
}
